import { useEffect, useRef } from 'react';
import { Button } from './controls';

/**
 * An alert that asks before something that cannot be taken back, as the Mac asks: a short
 * title, a line saying what will happen, Cancel, and the action named on its own button
 * in red. Return presses the button that has focus (Cancel, unless the action was tabbed to
 * or clicked), Escape cancels. A delete here is final, so a reflex Return never makes it.
 *
 * It owns the keyboard while it is up. The canvas listens on the window, and a Delete or
 * an arrow pressed at an alert must not reach the drawing behind it.
 */
export function ConfirmAlert({
  title,
  message,
  action,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  action: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Focus starts on Cancel, so a stray Space does not destroy anything.
    cancelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') return;
      event.stopImmediatePropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (document.activeElement === actionRef.current) onConfirm();
        else onCancel();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel, onConfirm]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/20 px-[16px]" onPointerDown={(event) => event.target === event.currentTarget && onCancel()}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-message" className="glass-thick ui-pop w-full max-w-[300px] rounded-[18px] p-[18px] text-center text-label">
        <h2 id="confirm-title" className="text-[15px] font-semibold">
          {title}
        </h2>
        <p id="confirm-message" className="mt-[6px] text-[13px] text-label-2">
          {message}
        </p>
        <div className="mt-[16px] grid grid-cols-2 gap-[8px]">
          <Button ref={cancelRef} full onClick={onCancel}>
            Cancel
          </Button>
          <Button ref={actionRef} variant="destructive" full onClick={onConfirm}>
            {action}
          </Button>
        </div>
      </div>
    </div>
  );
}
